import { useCallback, useEffect, useState } from 'react';
import { downloadWithToken, openWithToken, request } from '../../services/api';

const emptyContract = {
  contract_template_id: '',
  expires_at: '',
};

export default function ContractsPage({ token }) {
  const [templates, setTemplates] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [form, setForm] = useState(emptyContract);
  const [message, setMessage] = useState('');
  const [selectedSigner, setSelectedSigner] = useState(null);

  const load = useCallback(() => {
    request('/api/contracts/templates?activeOnly=true', { token }).then((response) => setTemplates(response.data));
    request('/api/contracts', { token }).then((response) => setContracts(response.data));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    const response = await request('/api/contracts', {
      token,
      method: 'POST',
      body: { ...form, expires_at: form.expires_at || null },
    });
    setMessage(`Public signing link generated: ${response.data.public_url}`);
    setForm(emptyContract);
    load();
    setTimeout(() => setMessage(''), 5000);
  };

  const deleteContract = async (id) => {
    if (!window.confirm('Delete this signing-link history? This cannot be undone.')) return;
    await request(`/api/contracts/${id}`, { token, method: 'DELETE' });
    setMessage('Signing-link history deleted.');
    load();
    setTimeout(() => setMessage(''), 4000);
  };

  return (
    <div className="stack">
      {message && <div className="alert success">{message}</div>}

      <section className="panel">
        <div className="panel-header-actions">
          <div>
            <h2>Generate Public Signing Link</h2>
            <p className="panel-subtitle">Create a unique, secure, and shareable link for client signing.</p>
          </div>
        </div>
        <form onSubmit={submit} className="form-grid compact-form">
          <label>PDF contract template
            <select value={form.contract_template_id} onChange={(e) => setForm({ ...form, contract_template_id: e.target.value })} required>
              <option value="">Select PDF contract template...</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>{template.title}</option>
              ))}
            </select>
          </label>
          <label>Link Expiration Date & Time (Optional)
            <input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
          </label>
          <button className="primary">Create public signing link</button>
        </form>
      </section>

      <section className="panel">
        <div className="panel-header-actions">
          <div>
            <h2>Signing-Link History</h2>
            <p className="panel-subtitle">Monitor sent links, review signature status, and audit completed contracts.</p>
          </div>
        </div>

        {!contracts.length ? (
          <div className="empty">No links generated yet. Use the form above to generate your first signing link.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Contract</th>
                  <th>Signer Details</th>
                  <th>Status</th>
                  <th>Public Link</th>
                  <th>Files & Audit</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {contracts.map((contract) => (
                  <tr key={contract.id}>
                    <td>
                      <div className="template-info-cell">
                        <strong>{contract.title}</strong>
                        <span className="template-desc-sub">Created: {new Date(contract.created_at).toLocaleDateString()}</span>
                      </div>
                    </td>
                    <td>
                      <div className="template-info-cell">
                        {contract.recipient_full_name ? (
                          <>
                            <strong>{contract.recipient_full_name}</strong>
                            <span className="template-desc-sub">{contract.recipient_email}</span>
                          </>
                        ) : (
                          <span className="muted">Pending signer completion</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${contract.status}`}>{contract.status}</span>
                    </td>
                    <td>
                      <button type="button" onClick={() => { navigator.clipboard.writeText(contract.public_url); alert('Link copied to clipboard!'); }}>
                        📋 Copy Link
                      </button>
                    </td>
                    <td className="actions">
                      <button type="button" onClick={() => openWithToken(`/api/contracts/${contract.id}/preview`, token)}>
                        👁️ Preview
                      </button>
                      <button type="button" onClick={() => downloadWithToken(`/api/contracts/${contract.id}/download`, token)}>
                        📄 PDF
                      </button>
                      {contract.signed_pdf_path ? (
                        <>
                          <button type="button" className="primary" onClick={() => downloadWithToken(`/api/contracts/${contract.id}/download-signed`, token)}>
                            Signed PDF
                          </button>
                          <button type="button" onClick={() => setSelectedSigner(contract)}>
                            🔍 Signer Info
                          </button>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <button type="button" className="danger-button" onClick={() => deleteContract(contract.id)}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Signer verification audit modal */}
      {selectedSigner && (
        <div className="modal-backdrop" onClick={() => setSelectedSigner(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>🔍 Secure E-Sign Audit Details</h3>
              <button type="button" className="close-btn" onClick={() => setSelectedSigner(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="profile-details-grid">
                <div className="detail-item">
                  <strong>Document Title</strong>
                  <span>{selectedSigner.title}</span>
                </div>
                <div className="detail-item">
                  <strong>Signer Legal Name</strong>
                  <span>{selectedSigner.recipient_full_name || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Verified Email</strong>
                  <span>{selectedSigner.recipient_email || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Verified Phone</strong>
                  <span>{selectedSigner.recipient_phone || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Physical Address</strong>
                  <span>{selectedSigner.recipient_address || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>National ID / NIDA</strong>
                  <span>{selectedSigner.recipient_national_id || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Emergency Contact</strong>
                  <span>{selectedSigner.emergency_contact || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Signing IP Address</strong>
                  <span>{selectedSigner.employee_ip || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Execution Timestamp</strong>
                  <span>{selectedSigner.signed_at ? new Date(selectedSigner.signed_at).toLocaleString() : 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <strong>Audit Status</strong>
                  <span className="secure-badge">COMPLETED & AUDITED</span>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="ghost" onClick={() => setSelectedSigner(null)}>
                Close Audit Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
